<template>
  <div class="sh-login-page-container">
    <aside class="sh-login-left-panel">
      <div class="sh-left-panel-content">
        <div class="sh-logo-section">
          <img
            class="sh-logo-image"
            src="/rallyhood-white.svg"
            alt="Rallyhood"
          >
        </div>

        <div class="sh-welcome-block sh-left-panel-desktop-only">
          <h1 class="sh-welcome-heading-top">
            Welcome to Rallyhood!
          </h1>
          <p class="sh-login-prompt">
            Create an account to dive right into creating events, collaborating with others, and all the exciting features waiting for you!
          </p>

          <p class="sh-login-prompt">
            Want to make organizing even easier? Upgrade to <a
              href="https://www.rallyhood.com/home/request-a-demo.php"
              target="_blank"
              rel="noopener"
              class="premium-text"
            >Premium</a> for smarter tools that help you plan, connect, and rally your people like a pro.
          </p>
        </div>
      </div>
    </aside>

    <section class="sh-login-right-panel">
      <div class="sh-right-panel-content">
        <div>
          <h2 class="sh-login-form-heading">
            Create An Account
          </h2>

          <form>
            <p
              v-if="accountStatus.error"
              style="color:red;text-align:center"
            >
              Account Creation Error Occured: <br> {{
                userFacingMessage }} <br>
              <router-link
                v-if="!$route.query.createacc"
                class="link-button"
                to="/login"
              >
                Please Log In
                Here
              </router-link>
              <router-link
                v-if="$route.query.createacc"
                class="link-button"
                :to="'/' + $route.query.createacc"
              >
                Please Log In Here To Join Rally
              </router-link>
            </p>
            <p
              v-if="userFacingMessage && userFacingMessage.includes('exists')"
              style="text-align:center"
            />

            <div class="form-row">
              <div class="form-group half">
                <label
                  for="firstName"
                  class="dg text-align-left"
                >First Name</label>
                <input
                  id="firstName"
                  v-model="firstName"
                  type="text"
                  required
                  :class="['dg',{ 'input-error': errorMessages.firstName }]"
                >

                <span
                  v-show="errorMessages.firstName"
                  class="dg error-text"
                >{{ errorMessages.firstName }}</span>
              </div>
              <div class="form-group half">
                <label
                  for="lastName"
                  class="dg text-align-left"
                >Last Name</label>
                <input
                  id="lastName"
                  v-model="lastName"
                  type="text"
                  required
                  :class="['dg',{ 'input-error': errorMessages.lastName }]"
                >

                <span
                  v-show="errorMessages.lastName"
                  class="dg error-text"
                >{{ errorMessages.lastName }}</span>
              </div>
            </div>

            <div class="form-group">
              <label
                for="email"
                class="dg text-align-left"
              >Email</label>
              <input
                id="email"
                v-model="email"
                type="text"
                required
                :class="['dg',{ 'input-error': errorMessages.email }]"
              >

              <span
                v-show="errorMessages.email"
                class="dg error-text"
              >{{ errorMessages.email }}</span>
            </div>
            <div class="form-group">
              <label
                for="password"
                class="dg text-align-left"
              >Password</label>
              <div class="password-input-container">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  autocomplete="current-password"
                  class="password-input"
                  :class="['dg',{'input-error': errorMessages.password }]"
                  @click="scrollTop"
                >
                <button
                  type="button"
                  class="sh-toggle-password"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  @click="togglePasswordVisibility('password')"
                >
                  <i
                    :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <p class="dg text-muted">
                Your password must be at least 8 characters and contain one number, one special character, and one capital letter.
              </p>

              <span
                v-show="errorMessages.password"
                class="dg error-text"
              >{{ errorMessages.password }}</span>
            </div>
            <div class="form-group">
              <label
                for="password"
                class="dg text-align-left"
              >Confirm Password</label>
              <div class="password-input-container">
                <input
                  id="password"
                  v-model="passwordConfirmation"
                  :type="showPasswordConfirmation ? 'text' : 'password'"
                  name="passwordConfirmation"
                  autocomplete="current-password"
                  class="password-input"
                  :class="['dg', { 'input-error': errorMessages.passwordConfirmation }]" 
                  @click="scrollTop"
                >

                <button
                  type="button"
                  class="sh-toggle-password"
                  :aria-label="showPasswordConfirmation ? 'Hide password' : 'Show password'"
                  @click="togglePasswordVisibility('passwordConfirmation')"
                >
                  <i
                    :class="showPasswordConfirmation ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                    aria-hidden="true"
                  />
                </button>
              </div>

              <span
                v-show="errorMessages.passwordConfirmation"
                class="dg error-text"
              >{{ errorMessages.passwordConfirmation }}</span>
            </div>
            <div class="form-group">
              <label
                for="timezone"
                class="dg text-align-left"
              >Time Zone</label>
              <select
                id="timezone"
                v-model="timezone"
                required
                :class="['dg',{ 'input-error': errorMessages.timezone }]"
                @blur="validateTimezone"
              >
                <option
                  v-for="t in timezoneEntryList"
                  :key="t[0]"
                  :value="t[1]"
                  :disabled="t[0].includes('--------------------------')"
                >
                  {{ t[0] }}
                </option>
              </select>

              <span
                v-show="errorMessages.timezone"
                class="dg error-text"
              >{{ errorMessages.timezone }}</span>
            </div>

            <p class="dg text-centered flex justify-content-center" style="gap: 5px">
              <input type="checkbox" name="terms" v-model="terms_agreed" />
              I accept the

              <!-- Uncomment the following lines once the EULA is ready -->
              <!-- <a href="https://www.rallyhood.com/home/terms.php" target="_blank" rel="noopener" class="link-button">EULA</a>
              and -->
              <a href="https://www.rallyhood.com/home/terms.php" target="_blank" rel="noopener" class="link-button">Terms of Service</a>
            </p>

            <div class="actions">
              <button
                class="primary rally-join create-btn"
                :disabled="loading || !terms_agreed"
                @click.prevent="createMyAccount"
              >
                Create Account
              </button>

            </div>
            <div class="trouble" style="margin-top: 10px">
              <div>
                <div
                  v-if="!$route.query.createacc"
                  class="dg flex justify-content-center"
                >
                  <span>Already Have an Account? &nbsp;</span>
                  <router-link
                    class="link-button"
                    to="/login"
                  >
                    Log In
                  </router-link>
                </div>

                <div v-if="$route.query.createacc">
                  <span>Already Have an Account? &nbsp;</span>
                  <router-link
                    class="link-button"
                    :to="'/' + $route.query.createacc"
                  >
                    Log In
                  </router-link>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="sh-right-footer">
          <a href="mailto:support@rallyhood.com">Contact Us</a>
          <span
            class="sh-footer-sep"
            aria-hidden="true"
          >&bull;</span>
          <a
            href="https://www.rallyhood.com/home/terms.php"
            target="_blank"
            rel="noopener"
          >Terms &amp; Conditions</a>
          <span
            class="sh-footer-sep"
            aria-hidden="true"
          >&bull;</span>
          <a
            href="https://www.rallyhood.com/home/privacy"
            target="_blank"
            rel="noopener"
          >Privacy</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from "vuex";
  import {userInfo} from "@/common/user-info";
  import {TIMEZONE_MAPPING, isPasswordValid} from "@/common";  
  import { Plugins } from '@capacitor/core';
  import { Preferences } from '@capacitor/preferences';
  import { useBreakpoint } from '@/composables/useBreakpoint';
  import moment from "moment-timezone";
import { timezoneAutoMapper } from "../common";
  
  export default {
    data() {
      return {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        timezone: '',
        errorMessages: {firstName: '', lastName: '', email: '', password: '', passwordConfirmation: '', timezone: ''},
        timezoneValid: false, // Flag to track timezone validity
        passwordMatchValid: false,
        passwordComplexityValid: false,// Array to store error messages
        showPassword: false,
        showPasswordConfirmation: false,
        loading: false,
        breakpoint: useBreakpoint(),
        terms_agreed: false
      };
    },
    computed: {
      ...mapState("user", ["accountStatus"]),
      timezoneEntryList(){
        return Object.entries(TIMEZONE_MAPPING)
      },
      currentUser() {
      return this.usersById[this.user_id];
      },
      userFacingMessage() {
        if (this.accountStatus.errorMessage && this.accountStatus.errorMessage.indexOf("emails.email has already been taken") >= 0){
            return "A user account exists with that email."
        } else {
          return this.accountStatus.errorMessage
        }
      },
    },

    watch: {
      firstName() {
        this.errorMessages.firstName = this.firstName.trim()
          ? ""
          : "Please enter your first name";
      },

      lastName() {
        this.errorMessages.lastName = this.lastName.trim()
          ? ""
          : "Please enter your last name";
      },

      email() {
        if (!this.email.trim()) {
          this.errorMessages.email = "Please enter a valid email address";
        } else {
          this.errorMessages.email = "";
        }
      },

      password() {
        this.validatePassword();
        this.validatePasswordMatch();

        this.errorMessages.password = "";

        if (!this.password.trim()) {
          this.errorMessages.password = "Invalid password";
        }

        this.errorMessages.passwordConfirmation =
          this.passwordMatchValid ? "" : "Passwords must match";
      },

      passwordConfirmation() {
        this.validatePasswordMatch();

        this.errorMessages.passwordConfirmation =
          this.passwordMatchValid ? "" : "Passwords must match";
      },

      timezone() {
        this.validateTimezone();
         this.errorMessages.timezone = this.timezoneValid ? "" : "Please select a time zone";
       }
      },
      created(){

      },

    mounted() {
      console.log(userInfo())
      console.log(this.$route.query.createacc )
      if (this.$route.query.createacc && userInfo() && userInfo().user_id){
          console.log("not clearing state")
          this.$router.push({
            path: "/" + this.$route.query.createacc
          })
      } else {
        console.log("clearing state")
        this.logout()
      }

      this.setDetectedTimezone();
    },
    methods: {
      ...mapActions("user", ["createAccount", "clearUsersState"]),
      togglePasswordVisibility(field) {
        if (field === 'password') {
          this.showPassword = !this.showPassword
        } else if (field === 'passwordConfirmation') {
          this.showPasswordConfirmation = !this.showPasswordConfirmation
        }
      },
      scrollBack(){
        this.$refs["loginform"].style = ''
      },
      scrollTop(){
        if (this.screenWidth < 960) {
          this.$refs["loginform"].style = 'position: fixed; top: 25px; width: 100%'
        }
      },
      scrollToTop () {
        this.$el.scrollTop = 0
      },
      async logout(){
        localStorage.removeItem("at");
        localStorage.removeItem("user");
        await Preferences.remove({ key: 'user' });
      },
      async createMyAccount() {
        this.logout()
        if (!this.validateForm()) {
          return;
        }
        const userData = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          timezone: this.timezone,
          password: this.password,
          terms_agreed: this.terms_agreed
        };

        this.loading = true;
        await this.createAccount(userData);
        this.loading = false;

        if (!this.accountStatus.error){
          this.$store.dispatch('user/flashSuccess', 'Account Created!');
          console.log("redirect.... " +  this.$route.query.createacc)
          if (this.$route.query.createacc){
            this.$router.push({
              path: "/" + this.$route.query.createacc
            })
          } else {
            this.navToRallies();
          }
        }
      },
      validateForm() {
        // Reset error messages
        this.errorMessages = {firstName: '', lastName: '', email: '', password: '', passwordConfirmation: '', timezone: ''};
        try {
          this.validatePassword()
          this.validatePasswordMatch()
          this.validateTimezone()
        } catch (e){
          console.log(e)
        }
        // Check if any required field is empty
        if (!this.firstName.trim()) {
          this.errorMessages.firstName = "First Name is required.";
        }
        if (!this.lastName.trim()) {
          this.errorMessages.lastName = "Last Name is required.";
        }
        if (!this.email.trim()) {
          this.errorMessages.email = "Email is required.";
        }
        if (!this.timezoneValid) {
          this.errorMessages.timezone = "Timezone is required.";
        }
        if (!this.password.trim()) {
          this.errorMessages.password = "Password is required.";
        }
        if (!this.passwordMatchValid || this.passwordConfirmation.trim().length === 0) {
          this.errorMessages.passwordConfirmation = "Passwords do not match.";
        }
        if (!this.passwordComplexityValid) {
          this.errorMessages.password = "Invalid password";
        }
        // Return true if the form is valid, otherwise return false
        return Object.values(this.errorMessages).every((msg) => !msg);
      },
      validatePassword() {
        // Validate password complexity on each input change
        this.passwordComplexityValid = isPasswordValid(this.password);
      },
      validatePasswordMatch() {
        // Validate password match when passwordConfirmation field is changed
        this.passwordMatchValid = this.password === this.passwordConfirmation;
      },
      validateTimezone() {
        // Validate timezone when the field loses focus (blurred)
        this.timezoneValid = !!this.timezone;
      },
      navToRallies(){
         this.$router.push("/rallies")
      },
      //just set it once and don't override user selections
      setDetectedTimezone() {
        const { mappedTz } = timezoneAutoMapper();
        if (!this.timezone && mappedTz) {
          this.timezone = mappedTz;
        }
      }
    }
    
  };
</script>
<style scoped>
.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input {
  flex-grow: 1;
  padding-right: 60px; /* Adjust as needed for space for the toggle link */
}

.toggle-password {
  color: #008295;
  position: absolute;
  right: 10px; /* Adjust as needed for exact positioning */
  cursor: pointer;
  padding: 5px;
  text-decoration: none;
}

.create-btn {
  font-weight: 700;
  text-transform: initial;
  width: 100%;
}

.form-row {
    display: flex;
    gap: 16px;
}

.form-group.half {
    flex: 1;
    width: auto;
}

.premium-text {
  color: white
}

</style>
